import {
  BG_STYLES,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import React, { useContext, useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  ScrollView,
} from 'react-native'

import fetchAllCrops from '../../apis/fetchAllCrops'
import { Crop, CropsWithCategory } from '../../models/crop'

import updateFarmerCrops from '../../apis/updateFarmerCrops'
import ToastAndroid from 'app/mocks/ToastAndroid'

import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import SearchInput from 'app/components/reusable/SearchInput'
import RoundImageWithName from 'app/components/reusable/RoundImageWithName'
import RoundImageWithIcon from 'app/components/reusable/RoundImageWithIcon'
import { DIMENSION_HEIGHT, isMobileDevice } from 'app/utils/common'

interface CropSelectionWithFlagProps {
  farmerId: string
  userCrops: Crop[]
  onClose: () => void
  onSubmit: (crops: string) => void
}

const CropSelectionComponent = ({
  farmerId,
  userCrops,
  onClose,
  onSubmit,
}: CropSelectionWithFlagProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const [loading, setLoading] = useState(true)
  const [allCrops, setAllCrops] = useState<CropsWithCategory[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [cropsToBeSearched, setCropsToBeSearched] = useState<Crop[]>([])
  const isDesktop = !isMobileDevice()

  const [selectedCrops, setSelectedCrops] = useState<Record<string, Crop>>(
    () => {
      return userCrops.reduce((data: Record<string, Crop>, crop) => {
        data[crop.id] = crop
        return data
      }, {})
    },
  )

  const searchResults = cropsToBeSearched.filter(crop =>
    crop.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const isCropModified =
    userCrops
      .map(crop => crop.id)
      .sort()
      .join() === Object.keys(selectedCrops).sort().join()

  const fetchAndUpdateAllCrops = async () => {
    const response = await fetchAllCrops()
    if (response) {
      setAllCrops(response)
      const onlyCrops: Crop[] = []
      response.forEach(data => onlyCrops.push(...data.crops))
      setCropsToBeSearched(onlyCrops)
    }
  }

  const handleCropClick = (crop: Crop) => {
    const crops = { ...selectedCrops }
    if (selectedCrops[crop.id]) {
      delete crops[crop.id]
    } else {
      crops[crop.id] = crop
    }
    setSelectedCrops(crops)
  }

  const handleSubmitCrops = async () => {
    setLoading(true)
    const success = await updateFarmerCrops(
      farmerId,
      Object.keys(selectedCrops),
    )
    if (success) {
      ToastAndroid.show('Crops Upated Successfully!', ToastAndroid.SHORT)
      onSubmit(
        Object.values(selectedCrops)
          .map(crop => crop.name)
          .join(),
      )
    } else {
      ToastAndroid.show('Something went wrong. Please try ', ToastAndroid.SHORT)
    }
  }

  useEffect(() => {
    fetchAndUpdateAllCrops().finally(() => setLoading(false))
  }, [])

  return (
    <Modal>
      <ScrollView
        contentContainerStyle={[
          COMMON_STYLES.fullHeight,
          mediaStyles({ desktopStyle: COMMON_STYLES.row }),
        ]}
      >
        <View
          style={[
            COMMON_STYLES.pv2,
            mediaStyles({
              desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
              mobileStyle: COMMON_STYLES.ph1,
            }),
          ]}
        >
          <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
            <Text
              style={[
                TYPOGRAPHY.HEADING1,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              Crops Grown By Farmer
            </Text>
            {!isDesktop && (
              <Pressable onPress={onClose}>
                <CrossIcon height={32} width={32} />
              </Pressable>
            )}
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            horizontal={isMobileDevice()}
          >
            <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
              {Object.values(selectedCrops).map(crop => (
                <RoundImageWithIcon
                  crop={crop}
                  key={crop.id}
                  containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                  activeImagecontainerStyle={styles.selectedImageContainer}
                  isIcon
                  imageStyle={styles.image}
                  iconContainerStyle={styles.crossStyle}
                  icon={
                    <CrossIcon height={16} width={16} strokeColor={'white'} />
                  }
                  onClick={() => handleCropClick(crop)}
                  onIconClick={() => handleCropClick(crop)}
                  isImageSelected={true}
                />
              ))}
            </View>
          </ScrollView>
        </View>

        <View
          style={[
            mediaStyles({
              desktopStyle: styles.divider,
              mobileStyle: styles.dividerHz,
            }),
          ]}
        />
        <View
          style={[
            COMMON_STYLES.pt2,
            COMMON_STYLES.flex1,
            mediaStyles({
              desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
              mobileStyle: COMMON_STYLES.ph1,
            }),
          ]}
        >
          {loading && <Spinner type="on-screen" />}
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.fullWidth,
              COMMON_STYLES.justifyBetween,
            ]}
          >
            <Text
              style={[
                TYPOGRAPHY.HEADING1,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              Choose Crops
            </Text>
            {isDesktop && (
              <Pressable onPress={onClose}>
                <CrossIcon height={32} width={32} />
              </Pressable>
            )}
          </View>
          <SearchInput
            placeholder={'Search crops'}
            onChange={(text: string) => setSearchQuery(text)}
            value={searchQuery}
            inputContainerStyle={COMMON_STYLES.mt1}
          />

          {!searchQuery ? (
            <>
              <ScrollView style={[COMMON_STYLES['mt5/4'], COMMON_STYLES.flex1]}>
                {allCrops.map((cat, index) => (
                  <View key={index}>
                    <Text
                      style={[
                        TYPOGRAPHY.TEXT1,
                        VARIANTS_STYLES.MEDIUM,
                        TEXT_STYLES.textNeutral80,
                      ]}
                    >
                      {cat.category}
                    </Text>
                    <View
                      style={[
                        COMMON_STYLES.row,
                        COMMON_STYLES.pv1,
                        COMMON_STYLES['ph5/4'],
                        COMMON_STYLES.justifyStart,
                        BG_STYLES.bgNeutral10,
                        COMMON_STYLES['mv3/4'],
                        COMMON_STYLES.br1,
                        COMMON_STYLES.flexWrap,
                      ]}
                    >
                      {cat.crops.map(crop => (
                        <RoundImageWithIcon
                          crop={crop}
                          key={crop.id}
                          containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                          activeImagecontainerStyle={
                            styles.selectedImageContainer
                          }
                          isIcon
                          imageStyle={styles.image}
                          iconContainerStyle={styles.crossStyle}
                          icon={
                            <CrossIcon
                              height={16}
                              width={16}
                              strokeColor={'white'}
                            />
                          }
                          onClick={() => handleCropClick(crop)}
                          onIconClick={() => handleCropClick(crop)}
                          isImageSelected={!!selectedCrops[crop.id]}
                        />
                      ))}
                    </View>
                  </View>
                ))}
              </ScrollView>
              <View
                style={[COMMON_STYLES.row, COMMON_STYLES['pv1/2'], styles.gap1]}
              >
                <Button
                  onClick={onClose}
                  text={'Cancel'}
                  type={BUTTON_TYPE.SECONDARY}
                  size={SIZE.large}
                  buttonStyle={COMMON_STYLES.flex1}
                />
                <Button
                  onClick={handleSubmitCrops}
                  text={'Save'}
                  size={SIZE.large}
                  buttonStyle={COMMON_STYLES.flex1}
                  status={
                    isCropModified
                      ? BUTTON_STATUS.DISABLED
                      : BUTTON_STATUS.ACTIVE
                  }
                />
              </View>
            </>
          ) : (
            <>
              {searchResults.length ? (
                <>
                  <Text
                    style={[
                      COMMON_STYLES.mv1,
                      TYPOGRAPHY.HEADING3,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral70,
                    ]}
                  >
                    Search Results
                  </Text>
                  <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
                    {searchResults.map(crop => (
                      <RoundImageWithName
                        key={crop.id}
                        name={crop.name}
                        imgUrl={crop.image_url}
                        handleItemClick={() => {
                          handleCropClick(crop)
                          setSearchQuery('')
                        }}
                        style={COMMON_STYLES['mv3/4']}
                      />
                    ))}
                  </View>
                </>
              ) : (
                <Text
                  style={[
                    COMMON_STYLES.mv1,
                    TYPOGRAPHY.HEADING3,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral70,
                  ]}
                >
                  No Crop Found
                </Text>
              )}
            </>
          )}
        </View>
      </ScrollView>
    </Modal>
  )
}

const styles = StyleSheet.create({
  divider: {
    borderRightWidth: 1,
    borderColor: COLORS.neutral20,
    height: DIMENSION_HEIGHT,
  },
  dividerHz: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  'ph5/2': {
    paddingHorizontal: 40,
  },

  selectedCropContainer: {
    width: 400,
  },

  margin: {
    marginHorizontal: 10,
    marginBottom: 4,
    width: 80,
  },

  image: {
    height: 80,
    width: 80,
  },

  selectedImageContainer: {
    borderColor: COLORS.primary100,
    borderWidth: 2,
  },

  crossStyle: {
    position: 'absolute',
    top: 0,
    right: -3,
    zIndex: 1,
    padding: 5,
    borderRadius: 9999,
    backgroundColor: COLORS.primary100,
    borderWidth: 2,
    borderColor: COLORS.white,
  },

  gap1: {
    gap: 16,
  },
})

export default CropSelectionComponent
